import React from "react"
import types from "prop-types"
import { isEmpty } from "lodash-es"
import { useCustomName, useSCIMResourceManagement, useUserDiscardRelatedAudits } from "hooks"
import { DEPARTMENT_KEY } from "utils/customNameHelpers"
import Switch from "shared/Switch"
import ItemRow from "shared/ItemRow"
import LoadingIndicator from "shared/LoadingIndicator"
import * as API from "services/api"
import { errorToast } from "shared/toast"
import { SUBMITTER_ROLE } from "utils/userRoleHelpers"
import AuditItem from "./AuditItem"

const submitterConfirmationPrompt = (fullName) => `Are you sure you'd like to deactivate ${fullName}?`
  + "\n\n"
  + "When a submitter is deactivated, they can no longer "
  + "submit forms that require authentication."

const activityHubUserConfirmationPrompt = (fullName, departmentsInSentence) => `Are you sure you'd like to deactivate ${fullName}?`
  + "\n\n"
  + "When an Activity Hub user is deactivated, they are removed as members "
  + `from groups and ${departmentsInSentence} where they are a member.`
  + " They will be removed from workflows where they are individually assigned."
  + "\n\n"
  + `${fullName} will remain associated with Tasks to which they are assigned or are `
  + "a member, but they will appear inactive / grayed out and will not receive emails."

const activeStatusUpdater = (active) => (
  active ? API.activateUserAccount : API.deactivateUserAccount
)

const ACTIVE_STATUS = "user is active"
const INACTIVE_STATUS = "user is deactivated"

const SCIM_ENABLED_TOOLTIP = "managed via SCIM user provisioning"

const USER_UPDATE_ERROR_MESSAGE = "Something went wrong. Unable to activate user."

const confirmDeactivation = (role, fullName, departmentsInSentence) => (role === SUBMITTER_ROLE
  ? window.confirm(submitterConfirmationPrompt(fullName))
  : window.confirm(activityHubUserConfirmationPrompt(fullName, departmentsInSentence)))

const UserActive = ({ user, updateActiveAndRole }) => {
  const { pluralNameInSentence: departmentsInSentence } = useCustomName(DEPARTMENT_KEY)

  const { usersManagedBySCIM } = useSCIMResourceManagement()
  const {
    audits, initializing, loadAudits, reloadAudits,
  } = useUserDiscardRelatedAudits(user.id)

  const status = user.active ? ACTIVE_STATUS : INACTIVE_STATUS
  const tooltipText = usersManagedBySCIM ? SCIM_ENABLED_TOOLTIP : ""

  const updateUserActive = async (active) => {
    if (active || confirmDeactivation(user.role, user.fullName, departmentsInSentence)) {
      const response = await activeStatusUpdater(active)({ userId: user.id })

      if (response.ok) {
        updateActiveAndRole(active, response.data.role)
        reloadAudits()
      } else {
        errorToast(USER_UPDATE_ERROR_MESSAGE, response)
      }
    }
  }

  const hasAudits = !isEmpty(audits)

  return (
    <ItemRow
      collapsable
      className="dark"
      onExpand={loadAudits}
      itemRowHeaderContent={(
        <>
          <span className="font-bold">Active Status</span>
          {
            /*
              With a tooltip, the switch arrives wrapped in Tooltip's inline span, which takes its
              height from the inherited line-height rather than from the switch.  Sizing the spans
              to their contents lines the two up.
            */
          }
          <div className="flex items-center gap-4 [&>span]:flex">
            <span className="text-xs text-dark">{status}</span>
            <Switch
              aria-label="Active Status"
              checked={user.active}
              disabled={usersManagedBySCIM}
              onChange={updateUserActive}
              tooltipText={tooltipText}
            />
          </div>
        </>
      )}
      itemRowBody={(
        <div className="flex flex-col gap-8">
          {
            initializing && <LoadingIndicator textValue="Loading activation history ..." />
          }
          {
            hasAudits && (
              <div className="max-h-60 overflow-y-auto thin-scrollbar transparent-scroll-track">
                {
                  audits.map((audit) => <AuditItem key={audit.id} audit={audit} />)
                }
              </div>
            )
          }
          <div className="flex flex-col gap-4 text-xs text-dark">
            {
              usersManagedBySCIM && (
                <p>
                  Users are managed via SCIM user provisioning.<br />
                  Please contact your internal support to activate and deactivate users.
                </p>
              )
            }
            <p>
              Deactivation acts as a soft delete. The account remains in the system,
              but the user may not sign in or access any data within the application.
            </p>
          </div>
        </div>
      )}
    />
  )
}

UserActive.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  user: types.object.isRequired,
  updateActiveAndRole: types.func.isRequired,
}

export default UserActive
